/**
 * Filtre du sélecteur de fichiers, par plateforme.
 *
 * L'attribut `accept` n'est pas interprété de la même façon partout, et une
 * valeur unique ne peut pas convenir aux cinq systèmes visés : ce qui rend la
 * sélection possible sur iOS est précisément ce qui la rend permissive
 * ailleurs.
 *
 *  - **Bureau — Windows, macOS, Linux, sur Chrome, Edge et Firefox.** Le
 *    sélecteur construit son filtre à partir des extensions. `.gpx` seul
 *    grise tout le reste. Y ajouter un type MIME que le système ne sait pas
 *    résoudre — `application/octet-stream` au premier chef — fait retomber la
 *    boîte de dialogue sur « tous les fichiers » : le filtre existe encore
 *    mais ne filtre plus rien.
 *  - **Android.** Le navigateur traduit `accept` en types MIME pour le
 *    gestionnaire de documents du système, qui grise ce qu'il ne reconnaît
 *    pas. `application/gpx+xml` est ce que passe l'application Android
 *    (`MainActivity.openGpxLauncher.launch`). Les types XML l'accompagnent
 *    parce qu'un fournisseur de stockage annonce parfois un GPX ainsi, et
 *    qu'un GPX alors grisé rendrait l'import impossible.
 *  - **iOS et iPadOS.** Le sélecteur ne filtre pas par extension mais par UTI,
 *    et ne connaît ni `.gpx` ni `application/gpx+xml` tant qu'aucune
 *    application installée ne les déclare. Sans un type qu'il sache résoudre,
 *    l'application Fichiers grise **tous** les fichiers et l'import devient
 *    impossible. `application/octet-stream` se résout en `public.data`, dont
 *    tout fichier hérite : la sélection redevient possible, au prix d'un
 *    filtre qui ne grise plus rien. **C'est une limite du système, pas un
 *    choix** — d'où le contrôle du contenu à l'import, qui est le seul garde
 *    restant sur cette plateforme.
 *
 * Le filtre n'est de toute façon qu'une indication : plusieurs systèmes
 * laissent choisir n'importe quel fichier malgré lui. `looksLikeGpx` tranche
 * après coup, sur le contenu.
 *
 * Il n'est en revanche pas responsable de l'absence des fournisseurs de
 * stockage en ligne dans le sélecteur — vérifié en le retirant, sans effet.
 * Cette voie passe par le partage vers l'application installée.
 */

/** Ce qu'il faut du navigateur pour reconnaître la plateforme. */
export interface PickerEnvironment {
  readonly userAgent: string
  readonly platform: string
  readonly maxTouchPoints: number
}

export type PickerPlatform = 'ios' | 'android' | 'desktop'

/** Bureau : l'extension suffit, et rien d'autre ne doit l'élargir. */
const ACCEPT_DESKTOP = '.gpx'

/** Android : des types MIME, jamais `application/octet-stream`. */
const ACCEPT_ANDROID = '.gpx,application/gpx+xml,application/xml,text/xml'

/** iOS : un UTI que le système sache résoudre, sinon plus rien n'est choisissable. */
const ACCEPT_IOS =
  '.gpx,application/gpx+xml,application/xml,text/xml,application/octet-stream'

/**
 * Reconnaît la plateforme du sélecteur.
 *
 * Le doute profite à iOS : c'est la seule plateforme où un filtre trop étroit
 * n'est pas une gêne mais un blocage — plus aucun fichier n'est choisissable.
 * Une détection ratée doit donc coûter un filtre permissif, jamais un import
 * impossible. Seuls Android et le bureau, reconnus franchement, resserrent.
 */
export function detectPickerPlatform(env: PickerEnvironment): PickerPlatform {
  const ua = env.userAgent
  const platform = env.platform

  if (/iPad|iPhone|iPod/.test(platform) || /iPad|iPhone|iPod/.test(ua)) return 'ios'

  // iPadOS se présente comme un Mac de bureau depuis iPadOS 13 : même chaîne
  // d'agent, même `platform`. L'écran tactile est ce qui l'en distingue, et
  // s'y tromper coûterait un iPad où plus rien n'est sélectionnable.
  if (/Mac/.test(platform) && env.maxTouchPoints > 1) return 'ios'

  if (/Android/.test(ua)) return 'android'

  // Les trois systèmes de bureau, et eux seuls. Un agent inconnu — navigateur
  // embarqué, système exotique — retombe sur iOS par prudence.
  if (/Windows|Macintosh|Mac OS X|Linux|CrOS|X11/.test(ua)) return 'desktop'

  return 'ios'
}

/** L'attribut `accept` à poser sur le champ de sélection. */
export function gpxAcceptAttribute(env: PickerEnvironment): string {
  switch (detectPickerPlatform(env)) {
    case 'desktop':
      return ACCEPT_DESKTOP
    case 'android':
      return ACCEPT_ANDROID
    case 'ios':
      return ACCEPT_IOS
  }
}

/** Lit la plateforme depuis le navigateur courant. */
export function currentPickerEnvironment(): PickerEnvironment {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
  }
}
